import React, { useEffect, useState } from "react";
import { AiDisclaimer } from "../components/AiDisclaimer";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { RING_COLORS } from "../constants/brand";
import { BreathTimer } from "../components/BreathTimer";
import { ArrowLeft, CheckCircle2, Timer, Dumbbell } from "lucide-react";

const IMAGES = {
  nutrition: "https://images.pexels.com/photos/10506640/pexels-photo-10506640.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  exercise: "https://images.pexels.com/photos/11920628/pexels-photo-11920628.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  sleep: "https://images.pexels.com/photos/3807626/pexels-photo-3807626.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  breathing: "https://images.pexels.com/photos/7880262/pexels-photo-7880262.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cold: "https://images.unsplash.com/photo-1734117928667-c7f943a27e80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxpY2UlMjBiYXRoJTIwY29sZCUyMHBsdW5nZXxlbnwwfHx8fDE3ODc0ODI3NDF8MA&ixlib=rb-4.1.0&q=85",
  relationships: "https://images.pexels.com/photos/8445576/pexels-photo-8445576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const CONTENT = {
  nutrition: {
    it: { intro: "Mangiare per la longevità significa nutrire cellule e microbiota con cibo vero, colorato e poco processato.", steps: ["Riempi metà piatto di verdure di stagione.", "Aggiungi proteine di qualità (legumi, pesce, uova).", "Condisci con olio EVO a crudo.", "Evita zuccheri raffinati e ultra-processati."], tech: { label: "Regola pratica", value: "Piatto sano di Harvard: ½ verdura, ¼ proteine, ¼ cereali integrali." } },
    en: { intro: "Eating for longevity means feeding cells and microbiome with real, colourful, minimally processed food.", steps: ["Fill half your plate with seasonal vegetables.", "Add quality protein (legumes, fish, eggs).", "Dress with raw EVO oil.", "Avoid refined sugars and ultra-processed foods."], tech: { label: "Rule of thumb", value: "Harvard healthy plate: ½ veg, ¼ protein, ¼ whole grains." } },
  },
  exercise: {
    it: { intro: "Il movimento è il farmaco più potente per la longevità: costruisce muscolo, cuore e mente.", steps: ["Riscaldati 5 minuti (mobilità articolare).", "Alterna forza (2-3 volte/sett.) e cardio.", "Cura la tecnica prima del carico.", "Recupera: il muscolo cresce nel riposo."], tech: { label: "Dose settimanale (OMS)", value: "150 min di attività moderata + 2 sessioni di forza." } },
    en: { intro: "Movement is the most powerful longevity medicine: it builds muscle, heart and mind.", steps: ["Warm up 5 minutes (joint mobility).", "Alternate strength (2-3x/week) and cardio.", "Master technique before load.", "Recover: muscle grows during rest."], tech: { label: "Weekly dose (WHO)", value: "150 min moderate activity + 2 strength sessions." } },
  },
  sleep: {
    it: { intro: "Il sonno è quando il corpo ripara, consolida la memoria e ripulisce il cervello.", steps: ["Vai a letto e svegliati a orari regolari.", "Spegni gli schermi 60 minuti prima.", "Stanza buia, fresca e silenziosa.", "Niente caffeina dal pomeriggio."], tech: { label: "Obiettivo", value: "7-9 ore per notte, con orari stabili anche nel weekend." } },
    en: { intro: "Sleep is when the body repairs, consolidates memory and cleans the brain.", steps: ["Keep consistent bed and wake times.", "Turn off screens 60 minutes before.", "Dark, cool, quiet room.", "No caffeine from the afternoon."], tech: { label: "Target", value: "7-9 hours per night, stable times even on weekends." } },
  },
  breathing: {
    it: { intro: "Il respiro lento attiva il nervo vago, riduce stress e infiammazione.", steps: ["Siediti comodo, spalle rilassate.", "Inspira dal naso per 4 secondi.", "Espira lentamente per 6-7 secondi.", "Ripeti per 5 minuti."], tech: { label: "Tecnica", value: "Respirazione 4-7-8 o box breathing (4-4-4-4)." } },
    en: { intro: "Slow breathing activates the vagus nerve, lowering stress and inflammation.", steps: ["Sit comfortably, relax your shoulders.", "Inhale through the nose for 4 seconds.", "Exhale slowly for 6-7 seconds.", "Repeat for 5 minutes."], tech: { label: "Technique", value: "4-7-8 breathing or box breathing (4-4-4-4)." } },
  },
  cold: {
    it: { intro: "L'esposizione al freddo allena la resilienza, attiva il grasso bruno e rilascia noradrenalina.", steps: ["Inizia con una doccia tiepida.", "Chiudi con 20-30 secondi di acqua fredda.", "Respira lento e profondo, non trattenere.", "Aumenta gradualmente la durata."], tech: { label: "Sicurezza", value: "Evita se hai problemi cardiaci; consulta il medico prima di bagni di ghiaccio." } },
    en: { intro: "Cold exposure trains resilience, activates brown fat and releases noradrenaline.", steps: ["Start with a warm shower.", "Finish with 20-30 seconds of cold water.", "Breathe slow and deep, don't hold.", "Gradually increase the duration."], tech: { label: "Safety", value: "Avoid with heart conditions; consult a doctor before ice baths." } },
  },
  relationships: {
    it: { intro: "Le relazioni profonde sono tra i predittori più forti di una vita lunga e felice.", steps: ["Dedica tempo di qualità senza schermi.", "Ascolta con attenzione e presenza.", "Coltiva un legame ogni giorno (chiamata, abbraccio).", "Chiedi aiuto e offri il tuo."], tech: { label: "Evidenza", value: "Harvard Study of Adult Development: la qualità dei legami predice salute e longevità." } },
    en: { intro: "Deep relationships are among the strongest predictors of a long, happy life.", steps: ["Spend quality, screen-free time.", "Listen with attention and presence.", "Nurture one bond daily (a call, a hug).", "Ask for help and offer yours."], tech: { label: "Evidence", value: "Harvard Study of Adult Development: bond quality predicts health and longevity." } },
  },
};

export default function Learn() {
  const { pillar } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [canComplete, setCanComplete] = useState(false);

  const meta = AREA_META[pillar];
  const data = CONTENT[pillar]?.[lang] || CONTENT[pillar]?.it;
  const color = RING_COLORS[pillar] || "#3F6E85";
  const img = IMAGES[pillar];

  useEffect(() => {
    setCanComplete(false);
    const tmo = setTimeout(() => setCanComplete(true), 5000);
    return () => clearTimeout(tmo);
  }, [pillar]);

  if (!meta || !data) {
    return (
      <div className="App min-h-screen"><Nav />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <p className="font-serif text-3xl text-[#3F6E85] mb-3">{t("learn_not_found")}</p>
          <button onClick={() => navigate("/dashboard")} data-testid="learn-back" className="rounded-full bg-[#3F6E85] text-white px-6 py-2.5 text-sm font-semibold">← {t("back")}</button>
        </div>
      </div>
    );
  }
  const Icon = meta.icon;

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6E6B65] mb-6 hover:text-[#3F6E85]" data-testid="learn-back">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>

        <div className="rounded-[2rem] overflow-hidden border border-[#3F6E85]/10 bg-white" data-testid={`learn-${pillar}`}>
          <div className="relative h-52 sm:h-64" style={{ backgroundColor: color }}>
            {img && <img src={img} alt={t(pillar)} className="w-full h-full object-cover opacity-90" />}
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, ${color}` + "ee)" }} />
            <div className="absolute bottom-5 left-6 flex items-center gap-3 text-white">
              <span className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                <Icon className="w-6 h-6" strokeWidth={1.75} />
              </span>
              <h1 className="font-serif text-4xl tracking-tight">{t(pillar)}</h1>
            </div>
          </div>

          <div className="p-8">
            <p className="text-lg text-[#1A1D20]/80 leading-relaxed mb-8">{data.intro}</p>

            {pillar === "exercise" && (
              <button onClick={() => navigate("/program")} data-testid="today-task-box"
                className="w-full text-left rounded-2xl p-5 mb-8 flex items-center gap-3 text-white transition-transform hover:scale-[1.01]" style={{ backgroundColor: color }}>
                <Dumbbell className="w-6 h-6 shrink-0" />
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-[0.15em] font-bold opacity-90">{t("today_task")}</p>
                  <p className="font-semibold">{t("today_task_cta")}</p>
                </div>
                <ArrowLeft className="w-5 h-5 rotate-180" />
              </button>
            )}

            {pillar === "breathing" && (
              <div className="rounded-2xl bg-[#F3F1ED] mb-8 p-4" data-testid="breath-timer-wrap">
                <p className="text-center text-xs uppercase tracking-[0.15em] font-bold mb-1" style={{ color }}>{t("breath_coherence")}</p>
                <BreathTimer color={color} />
              </div>
            )}

            <div className="space-y-3 mb-8">
              {data.steps.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.12 }}
                  className="flex items-start gap-3 rounded-2xl bg-[#F3F1ED] p-4" data-testid={`learn-step-${i}`}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ backgroundColor: color }}>{i + 1}</span>
                  <p className="text-[#1A1D20] pt-0.5">{s}</p>
                </motion.div>
              ))}
            </div>

            <div className="rounded-2xl p-5 mb-8 border-2 border-dashed" style={{ borderColor: `${color}55` }} data-testid="learn-technique">
              <p className="text-xs uppercase tracking-[0.15em] font-bold mb-1" style={{ color }}>{data.tech.label}</p>
              <p className="text-[#1A1D20] font-medium">{data.tech.value}</p>
            </div>

            <AnimatePresence mode="wait">
              {canComplete ? (
                <motion.button key="done" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  onClick={() => navigate(`/track?pillar=${pillar}`)} data-testid="learn-register-done"
                  className="w-full rounded-full py-3.5 text-white font-semibold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: color }}>
                  <CheckCircle2 className="w-5 h-5" /> {t("register_as_done")}
                </motion.button>
              ) : (
                <div key="wait" className="w-full rounded-full py-3.5 bg-[#F3F1ED] text-[#6C757D] font-medium flex items-center justify-center gap-2" data-testid="learn-waiting">
                  <Timer className="w-4 h-4" /> {t("learn_read_first")}
                </div>
              )}
            </AnimatePresence>
            <AiDisclaimer />
          </div>
        </div>
      </div>
    </div>
  );
}
